import { ref, Ref } from 'vue';
import { merge } from 'lodash-es';
import { defineStore } from 'pinia';
import { AxiosResponse } from 'axios';

import apiGw from '@/services/apiGw';
import { Action, Method } from '@/types/general';
import { SseListener } from '@/utils/general';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { CardProgramContractsURI, CardProgramRsp, CardProgramURI } from '@/components/cards/types/cardProgram';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { createResourceUri, getUidsFromUri } from '@/utils/links';
import { PartnershipURI } from '@/types/partnership';

export const useCardProgramsStore = defineStore('cardPrograms', () => {
  const eventStreamStore = useEventStreamStore();

  // STATES
  const cardPrograms: Ref<CardProgramRsp[]> = ref([]);
  const cardProgramsInContext: Ref<Map<string, Set<string>>> = ref(new Map());
  const pendingPromises: Ref<{ [key: string]: Promise<AxiosResponse> }> = ref({});

  // GETTERS
  function getCardProgramsForContext(ctxUid: string) {
    if (!ctxUid) return [];
    return cardPrograms.value.filter((p) => cardProgramsInContext.value.get(ctxUid)?.has(p.program.cardProgramUid));
  }

  function isCardProgramStale(ctxUid: string) {
    return eventStreamStore.stale_card_programs && eventStreamStore.stale_card_programs[ctxUid];
  }

  function isCardProgramUriStale(uri: string) {
    return isCardProgramStale(getUidsFromUri(uri)[0]);
  }

  function hasAnyStaleCardProgramInContext(ctxUid: string) {
    const cardProgramsInCtx = cardProgramsInContext.value.get(ctxUid);
    if (cardProgramsInCtx) {
      return [...cardProgramsInCtx].some(
        (x) => eventStreamStore.stale_card_programs && Object.keys(eventStreamStore.stale_card_programs).includes(x)
      );
    }

    return false;
  }

  function canEditCardProgram(cp: CardProgramRsp) {
    return cp.actionables.myActionables.find((actionable) => actionable.action === 'capture');
  }

  function editCardProgramAction(cp: CardProgramRsp): Action | undefined {
    const captureAction = cp.actionables.myActionables.find((actionable) => actionable.action === 'capture');
    if (!captureAction) return undefined;

    return {
      method: captureAction.method.toLowerCase() as Method,
      uri: createResourceUri(CardProgramURI.BaseV2, {
        uid: cp.program.cardProgramUid
      })
    };
  }

  function bulkAddCardAction(cp: CardProgramRsp): Action | undefined {
    const captureAction = cp.actionables.myActionables.find((actionable) => actionable.action === 'capture');
    if (!captureAction) return undefined;

    return {
      method: Method.post,
      uri: createResourceUri(CardProgramURI.BaseV2, {
        uid: cp.program.cardProgramUid,
        resource: CardProgramURI.BulkAddCard
      })
    };
  }

  function getNumberOfActiveCardsInCardProgram(cps: CardProgramRsp[]) {
    let count = 0;
    cps.forEach((cp) => {
      const cards = cp?.numCards?.find((numInState) => numInState.entityStatus === 'active');
      count += cards?.count || 0;
    });
    return count;
  }

  function editCardProgramAdminsAction(cp: CardProgramRsp): Action | undefined {
    // TODO: is this correct? or should we use myActionables?
    if (!cp.contract.cardProgramContractUid) {
      return undefined;
    } else {
      return {
        method: Method.post,
        uri: createResourceUri(CardProgramContractsURI.BaseV2, {
          uid: cp.contract.cardProgramContractUid,
          resource: CardProgramContractsURI.ChangeAdmins
        })
      };
    }
  }

  function deleteCardProgramAction(cp: CardProgramRsp): Action | undefined {
    const deleteAction = cp.actionables.myActionables.find((actionable) => actionable.action === 'capture');
    if (!deleteAction) return undefined;

    return {
      method: Method.patch,
      uri: createResourceUri(CardProgramContractsURI.BaseV2, {
        uid: cp.contract.cardProgramContractUid
      })
    };
  }
  // ACTIONS
  function $reset() {
    cardPrograms.value = [];
    cardProgramsInContext.value = new Map();
    pendingPromises.value = {};
  }

  function appendOrReplaceCardPrograms(cps: Array<CardProgramRsp>) {
    cps.forEach((cp) => {
      const cardProgramIndex = cardPrograms.value.findIndex(
        (cardProgram) => cardProgram.program.cardProgramUid === cp.program.cardProgramUid
      );
      if (cardProgramIndex >= 0) {
        cardPrograms.value[cardProgramIndex] = cp;
      } else {
        cardPrograms.value.push(cp);
      }
    });
  }

  async function getCardProgramsForCurrentContext(forceReload?: boolean) {
    const contextSwitchStore = useContextSwitchStore();
    const currentContext = contextSwitchStore.getCurrentContext();

    if (currentContext) {
      const cardProgramUri = createResourceUri(PartnershipURI.BaseV2, {
        uid: currentContext.uid,
        resource: PartnershipURI.CardPrograms
      });

      await loadCardPrograms(cardProgramUri, forceReload);
      return getCardProgramsForContext(currentContext.uid);
    } else {
      return [];
    }
  }
  async function getCardProgramsForCurrentCashAccount(uri: string) {
    const programs = await getCardProgramsForCurrentContext();
    const filteredPrograms = [];
    if (programs) {
      for (const program of programs) {
        if (
          program.contract.allowedDebitAccounts.some(
            (account) => account.accountEnriched?.uid === getUidsFromUri(uri)[0]
          )
        ) {
          filteredPrograms.push(program);
        }
      }
      return filteredPrograms;
    }
  }

  async function loadCardPrograms(uri: string, forceReload?: boolean) {
    const promise = pendingPromises.value[uri];
    if (!!promise) {
      await promise;
    } else {
      const uid = getUidsFromUri(uri)[0];
      if (
        !forceReload &&
        cardProgramsInContext.value.has(uid) &&
        !hasAnyStaleCardProgramInContext(uid) &&
        !isCardProgramUriStale(uri)
      ) {
        return;
      }
      const request = apiGw.get(uri);
      pendingPromises.value[uri] = request;
      let cps: CardProgramRsp[] = (await request).data;
      // The BE is currently returning an empty string instead of an empty array when no card program exit.
      if (!Array.isArray(cps)) {
        cps = [];
      }
      const timeOfResponse = new Date().getTime();
      if (forceReload) {
        cardPrograms.value = [];
      }

      const previouslyLoadedUids = cardPrograms.value.map((cp) => cp.program.cardProgramUid);
      const newCardPrograms = cps.filter((cp) => !previouslyLoadedUids.includes(cp.program.cardProgramUid));
      appendOrReplaceCardPrograms(cps);

      const ctxUid = getUidsFromUri(uri)[0];
      const cpsInContext = cardProgramsInContext.value.get(ctxUid) || new Set();
      newCardPrograms.forEach((cp) => {
        cpsInContext.add(cp.program.cardProgramUid);
      });

      cardProgramsInContext.value.set(ctxUid, cpsInContext);

      const staleCpUids = eventStreamStore.stale_card_programs && Object.keys(eventStreamStore.stale_card_programs);

      cpsInContext.forEach((cpUid) => {
        if (staleCpUids) {
          if (staleCpUids.includes(cpUid)) {
            unstaleCardProgramUidIfOlder(cpUid, timeOfResponse);
          }
        }
      });
      if (isCardProgramUriStale(uri)) {
        /**
         * In case the cardProgram is marked as stale we can remove the stale mark but only if the
         * response came after we marked it as stale.
         */
        const uid = getUidsFromUri(uri)[0];
        unstaleCardProgramUidIfOlder(uid, timeOfResponse);
      }

      delete pendingPromises.value[uri];
    }
  }

  async function getCardProgramDetails(uid: string, forceReload?: boolean): Promise<CardProgramRsp> {
    const cardProgram = cardPrograms.value.filter((cp) => cp.program.cardProgramUid === uid)[0];
    if (cardProgram && !forceReload && !isCardProgramUriStale(uid)) {
      // defauldMonthly limit is only available in the details
      if (!!cardProgram.program.defaultATMMonthlyLimit) {
        return cardProgram;
      }
    }
    const response = await apiGw.get(createResourceUri(CardProgramURI.BaseV2, { uid: uid }));

    // Make sure we unstale the cardProgram as we just reloaded it
    const timeOfResponse = new Date().getTime();
    unstaleCardProgramUidIfOlder(uid, timeOfResponse);

    const cardProgramIndex = cardPrograms.value.findIndex((cp) => cp.program.cardProgramUid === uid);
    if (forceReload) {
      cardPrograms.value[cardProgramIndex] = response.data;
    } else {
      cardPrograms.value[cardProgramIndex] = merge(cardProgram, response.data);
    }

    return cardPrograms.value[cardProgramIndex];
  }

  function unstaleCardProgramUidIfOlder(uid: string, timeOfResponse: number) {
    if (!eventStreamStore.stale_card_programs) return;
    const staleTime = eventStreamStore.stale_card_programs[uid]?.time;
    if (staleTime < timeOfResponse) {
      eventStreamStore.unstaleUid(uid, SseListener.CardProgram);
    }
  }

  return {
    // STATES
    cardPrograms,
    pendingPromises,
    cardProgramsInContext,
    // GETTERS
    getCardProgramsForContext,
    canEditCardProgram,
    editCardProgramAction,
    editCardProgramAdminsAction,
    deleteCardProgramAction,
    bulkAddCardAction,
    getNumberOfActiveCardsInCardProgram,
    // ACTIONS
    $reset,
    loadCardPrograms,
    getCardProgramDetails,
    getCardProgramsForCurrentContext,
    getCardProgramsForCurrentCashAccount
  };
});
